// Shared building blocks for the platform console pages.
import { useEffect } from 'react'
import { X, Search } from 'lucide-react'
import { t, locale } from '../../i18n'

export const money = (amount, currency) => {
  try {
    return new Intl.NumberFormat(locale(), { style: 'currency', currency: currency || 'OMR', maximumFractionDigits: 2 }).format(Number(amount) || 0)
  } catch (e) {
    return `${(Number(amount) || 0).toFixed(2)} ${currency || ''}`
  }
}
export const cents = (value, currency) => money((Number(value) || 0) / 100, currency)

// Role names as shown to people ('cashier' -> 'Cashier').
export const roleLabel = role => t(role ? role[0].toUpperCase() + role.slice(1) : '')

const TONES = {
  green: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20',
  blue: 'bg-sky-50 text-sky-700 ring-sky-600/20',
  amber: 'bg-amber-50 text-amber-800 ring-amber-600/20',
  red: 'bg-rose-50 text-rose-700 ring-rose-600/20',
  violet: 'bg-violet-50 text-violet-700 ring-violet-600/20',
  gray: 'bg-slate-100 text-slate-600 ring-slate-500/20',
}
export function Chip({ tone = 'gray', children }) {
  return <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset whitespace-nowrap ${TONES[tone]}`}>{children}</span>
}

const SUBSCRIPTION = {
  active: ['green', 'Active'],
  trialing: ['blue', 'Trial'],
  past_due: ['amber', 'Past due'],
  unpaid: ['red', 'Unpaid'],
  canceled: ['gray', 'Canceled'],
  incomplete: ['red', 'Incomplete'],
  none: ['gray', 'No subscription'],
}
export function SubscriptionChip({ status }) {
  const [tone, label] = SUBSCRIPTION[status] || ['gray', status || 'No subscription']
  return <Chip tone={tone}>{t(label)}</Chip>
}

const TYPES = { restaurant: ['violet', 'Restaurant'], ecommerce: ['blue', 'Online store'], appointments: ['amber', 'Appointments'], rooms: ['green', 'Rooms'] }
export function TypeChip({ type }) {
  const [tone, label] = TYPES[type] || ['gray', type]
  return <Chip tone={tone}>{t(label)}</Chip>
}

export function StatCard({ label, value, hint, icon: Icon }) {
  return (
    <div className="rounded-xl bg-white p-5 ring-1 ring-slate-200">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm text-slate-500">{label}</p>
        {Icon && <Icon className="h-4 w-4 text-slate-400" />}
      </div>
      <p className="mt-2 text-2xl font-semibold text-slate-900 tabular-nums">{value}</p>
      {hint && <p className="mt-1 text-xs text-slate-500">{hint}</p>}
    </div>
  )
}

export function PageHeader({ title, subtitle, children }) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
      <div>
        <h1 className="text-xl font-semibold text-slate-900">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-slate-500">{subtitle}</p>}
      </div>
      {children}
    </div>
  )
}

export function SearchInput({ value, onChange, placeholder }) {
  return (
    <label className="relative block w-full sm:w-72">
      <Search className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
      <input
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full rounded-lg border-0 bg-white py-2 ps-9 pe-3 text-sm ring-1 ring-slate-200 placeholder:text-slate-400 focus:ring-2 focus:ring-indigo-500"
      />
    </label>
  )
}

export function Select({ value, onChange, children }) {
  return (
    <select value={value} onChange={e => onChange(e.target.value)}
      className="rounded-lg border-0 bg-white py-2 ps-3 pe-8 text-sm ring-1 ring-slate-200 focus:ring-2 focus:ring-indigo-500">
      {children}
    </select>
  )
}

export function Drawer({ open, onClose, title, children }) {
  useEffect(() => {
    if (!open) return
    const onKey = e => e.key === 'Escape' && onClose()
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])
  if (!open) return null
  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-slate-900/30" onClick={onClose} />
      <aside className="absolute inset-y-0 end-0 flex w-full max-w-xl flex-col bg-slate-50 shadow-xl">
        <div className="flex items-center justify-between gap-3 border-b border-slate-200 bg-white px-5 py-4">
          <h2 className="truncate text-base font-semibold text-slate-900">{title}</h2>
          <button onClick={onClose} className="rounded-md p-1 text-slate-500 hover:bg-slate-100" aria-label={t('Close')}><X className="h-5 w-5" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5">{children}</div>
      </aside>
    </div>
  )
}

export function Empty({ children }) {
  return <div className="rounded-xl bg-white px-6 py-12 text-center text-sm text-slate-500 ring-1 ring-slate-200">{children}</div>
}

export function Loading() {
  return <div className="flex justify-center py-16"><div className="loading-spinner" /></div>
}

// Table wrapper that scrolls sideways on small screens instead of the page.
export function Table({ head, children }) {
  return (
    <div className="overflow-x-auto rounded-xl bg-white ring-1 ring-slate-200">
      <table className="min-w-full divide-y divide-slate-200 text-sm">
        <thead className="bg-slate-50">
          <tr>{head.map((h, i) => <th key={i} className="whitespace-nowrap px-4 py-3 text-start text-xs font-medium uppercase tracking-wide text-slate-500">{h}</th>)}</tr>
        </thead>
        <tbody className="divide-y divide-slate-100">{children}</tbody>
      </table>
    </div>
  )
}
